/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { formatCurrency } from 'shared/formatCurrency';
import { tokens, orange, inkA } from '../tokens';

// Sales velocity histogram for the Box Office Report. Consumes the per-minute
// timeline the API returns ([{ time, count, revenue, sellers: [{ id, count,
// revenue }] }]) and re-buckets it into 5/10/15-minute intervals entirely on the
// client, so the interval toggle never refetches. Each bar is stacked by seller;
// the legend isolates a single seller. Seller names/colours come from the
// `sellers` prop (derived from the report's per-seller data). Bars are hand-built
// divs (no chart lib in this repo), styled from v3 tokens.

const INTERVALS = [5, 10, 15];

// Seller segment colours — brand orange first, then hues that read on both
// themes. Cycles if there are more sellers than colours.
const PALETTE = [
  '#fa4616',
  '#ff9e5e',
  '#ffd166',
  '#4cc9f0',
  '#a78bfa',
  '#4ade80',
  '#f472b6',
  '#38bdf8',
];

const dollars = (n) => formatCurrency(n, { cents: false });

// Per-minute ISO strings come back as UTC ('YYYY-MM-DDTHH:MM'); parse as UTC and
// label in the viewer's locale (on-site staff are in the event's zone).
const toMs = (time) => new Date(`${time}:00Z`).getTime();
const label = (ms) =>
  new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const metricOf = (obj, metric) =>
  (metric === 'revenue' ? obj.revenue : obj.count) || 0;

// Fold minute points into aligned interval buckets, keeping a per-seller split.
const bucketize = (points, interval, metric) => {
  const span = interval * 60 * 1000;
  const map = points.reduce((acc, p) => {
    const ms = toMs(p.time);
    if (Number.isNaN(ms)) return acc;
    const start = Math.floor(ms / span) * span;
    if (!acc[start]) acc[start] = { start, total: 0, sellers: {} };
    const bucket = acc[start];
    bucket.total += metricOf(p, metric);
    (p.sellers || []).forEach((s) => {
      bucket.sellers[s.id] = (bucket.sellers[s.id] || 0) + metricOf(s, metric);
    });
    return acc;
  }, {});
  return Object.keys(map)
    .map(Number)
    .sort((a, b) => a - b)
    .map((start) => map[start]);
};

const Pill = ({ active, onClick, children }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 15px',
        borderRadius: 16,
        cursor: 'pointer',
        background: active ? orange(0.1) : inkA(theme, 0.035),
        border:
          '1.5px solid ' +
          (active ? t.primary : hover ? inkA(theme, 0.28) : inkA(theme, 0.14)),
        color: active ? t.primary : inkA(theme, 0.62),
        fontSize: 13,
        fontWeight: active ? 700 : 600,
        transition: 'all 120ms ease',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {children}
    </button>
  );
};

const Dot = ({ color }) => (
  <span
    style={{
      width: 10,
      height: 10,
      borderRadius: 3,
      background: color,
      flexShrink: 0,
    }}
  />
);

const Bar = ({ bucket, peak, isPeak, isolate, order, meta, metric }) => {
  const t = tokens(useTheme());
  const total = isolate != null ? bucket.sellers[isolate] || 0 : bucket.total;
  const pct = peak > 0 ? (total / peak) * 100 : 0;
  const display =
    metric === 'revenue' ? dollars(total) : total.toLocaleString();

  // Segments bottom-up. Isolated view shows just the one seller.
  const ids = isolate != null ? [isolate] : order;
  const segments = ids
    .map((id) => ({ id, value: bucket.sellers[id] || 0 }))
    .filter((s) => s.value > 0);

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        height: '100%',
        justifyContent: 'flex-end',
      }}
    >
      <div
        style={{
          fontSize: 12.5,
          fontWeight: isPeak ? 800 : 600,
          color: isPeak ? t.text : t.sub,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {display}
      </div>
      <div
        style={{
          width: '100%',
          height: Math.max(pct, total > 0 ? 2 : 0) + '%',
          minHeight: total > 0 ? 4 : 0,
          borderRadius: '7px 7px 0 0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column-reverse',
          boxShadow: isPeak ? '0 0 22px ' + orange(0.4) : 'none',
          transition: 'height 200ms ease',
        }}
      >
        {segments.map((s) => (
          <div
            key={s.id}
            title={`${meta[s.id] ? meta[s.id].name : 'Seller'}: ${
              metric === 'revenue' ? dollars(s.value) : s.value.toLocaleString()
            }`}
            style={{
              width: '100%',
              height: (s.value / total) * 100 + '%',
              background: meta[s.id] ? meta[s.id].color : t.sub,
            }}
          />
        ))}
      </div>
      <div
        style={{
          fontSize: 12,
          fontWeight: isPeak ? 700 : 500,
          color: isPeak ? t.primary : t.sub,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {label(bucket.start)}
      </div>
    </div>
  );
};

const Stat = ({ label: l, value, accent }) => {
  const t = tokens(useTheme());
  return (
    <span style={{ fontSize: 13.5, color: t.sub, fontWeight: 500 }}>
      {l}:{' '}
      <strong
        style={{
          color: accent ? t.primary : t.text,
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </strong>
    </span>
  );
};

const SalesTimeline = ({ data = [], sellers = [] }) => {
  const t = tokens(useTheme());
  const [interval, setInterval] = useState(15);
  const [metric, setMetric] = useState('transactions');
  const [isolate, setIsolate] = useState(null); // seller id or null

  // Stable id order + name/colour per seller, from the report's seller list.
  const { order, meta } = useMemo(() => {
    const order = sellers.map((s) => s.id);
    const meta = sellers.reduce((acc, s, i) => {
      acc[s.id] = { name: s.name, color: PALETTE[i % PALETTE.length] };
      return acc;
    }, {});
    return { order, meta };
  }, [sellers]);

  const buckets = useMemo(
    () => bucketize(data, interval, metric),
    [data, interval, metric]
  );

  // Bar scale is always the unified peak so isolating reads as "a slice of".
  const peak = useMemo(
    () => buckets.reduce((mx, b) => Math.max(mx, b.total), 0),
    [buckets]
  );

  // Footer / peak-label values follow the current isolate selection.
  const { values, peakIdx, total, avg } = useMemo(() => {
    const values = buckets.map((b) =>
      isolate != null ? b.sellers[isolate] || 0 : b.total
    );
    let peakVal = 0;
    let peakIdx = -1;
    values.forEach((v, i) => {
      if (v > peakVal) {
        peakVal = v;
        peakIdx = i;
      }
    });
    const total = values.reduce((a, b) => a + b, 0);
    return {
      values,
      peakIdx,
      total,
      avg: values.length ? total / values.length : 0,
    };
  }, [buckets, isolate]);

  if (!data.length) return null;

  const fmt = (v) =>
    metric === 'revenue' ? dollars(v) : Math.round(v).toLocaleString();
  const noun = metric === 'revenue' ? 'revenue' : 'transactions';

  return (
    <div
      style={{
        background: t.card,
        border: '1px solid ' + t.border,
        borderRadius: 16,
        boxShadow: t.shadowCard,
        padding: '22px 24px',
        marginBottom: 24,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 20,
        }}
      >
        <div>
          <div style={{ fontSize: 19, fontWeight: 800, color: t.text }}>
            Sales Timeline
          </div>
          <div style={{ fontSize: 13.5, color: t.sub, marginTop: 3 }}>
            POS {noun} across the sales window
            {isolate != null && meta[isolate]
              ? ' · ' + meta[isolate].name
              : ' · by seller'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Pill
            active={metric === 'transactions'}
            onClick={() => setMetric('transactions')}
          >
            Transactions
          </Pill>
          <Pill
            active={metric === 'revenue'}
            onClick={() => setMetric('revenue')}
          >
            Revenue
          </Pill>
          <span style={{ width: 8 }} />
          {INTERVALS.map((m) => (
            <Pill
              key={m}
              active={interval === m}
              onClick={() => setInterval(m)}
            >
              {m} min
            </Pill>
          ))}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 10,
          height: 260,
          padding: '0 2px',
        }}
      >
        {buckets.map((b, i) => (
          <Bar
            key={b.start}
            bucket={b}
            peak={peak}
            isPeak={i === peakIdx}
            isolate={isolate}
            order={order}
            meta={meta}
            metric={metric}
          />
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 22,
          flexWrap: 'wrap',
          marginTop: 20,
          paddingTop: 18,
          borderTop: '1px solid ' + t.line,
        }}
      >
        <Stat
          label="Peak"
          value={fmt(values.length ? Math.max(...values) : 0)}
          accent
        />
        <span style={{ color: t.faint }}>·</span>
        <Stat label="Total" value={fmt(total)} />
        <span style={{ color: t.faint }}>·</span>
        <Stat label={`Avg/${interval}min`} value={fmt(avg)} />
      </div>

      {sellers.length ? (
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            marginTop: 18,
          }}
        >
          <Pill active={isolate == null} onClick={() => setIsolate(null)}>
            All sellers
          </Pill>
          {sellers.map((s) => (
            <Pill
              key={s.id}
              active={isolate === s.id}
              onClick={() => setIsolate(isolate === s.id ? null : s.id)}
            >
              <Dot color={meta[s.id] ? meta[s.id].color : t.sub} />
              {s.name}
            </Pill>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default SalesTimeline;
